import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { KeycafeAccessRow, KeycafeEventRow, ReservationRow } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { longDt } from '../lib/filters';

/**
 * The booking page's Keycafe card (docs/KEYCAFE.md): the code on the
 * booking — the same "Keycafe příjezd / odjezd" fields, still editable on
 * Opravit údaje when the wall misbehaves — what the wall last did with the
 * key, and every door opening KeyCafe reported for it. "Vytvořit klíč v
 * Keycafe" makes the key the dispatchers made by hand until now; it is
 * offered only while the booking has no code (an issued key is never
 * replaced) and the link is switched on for this deployment.
 */
const OUTCOME_LABEL: Record<string, string> = {
  arrival: 'příjezd zapsán',
  key_taken: 'klíč vyzvednut z boxu',
  key_returned: 'klíč vrácen do boxu',
  arrival_known: 'vhozen zákazníkem, příjezd už zapsán',
  would_arrival_known: 'vhozen zákazníkem, příjezd už zapsán (jen sledování)',
  departure: 'odjezd zapsán',
  would_arrival: 'příjezd (jen sledování)',
  would_key_taken: 'vyzvednut (jen sledování)',
  would_key_returned: 'vrácen (jen sledování)',
  would_departure: 'odjezd (jen sledování)',
  no_booking: 'bez rezervace',
  ignored: 'bez účinku',
  refused: 'odmítnuto',
  pending_arrival: 'příjezd se zapisuje',
  pending_departure: 'odjezd se zapisuje',
  review: 'ke kontrole',
};

/** The three codes (the owner, 26 October 2026), in the desk's words. */
const KIND_LABEL: Record<KeycafeAccessRow['kind'], string> = {
  dropoff: 'Zákazník — odevzdání',
  pickup: 'Zákazník — vyzvednutí',
  service: 'Servisní kód (dispečink, řidiči)',
};

function accessState(a: KeycafeAccessRow, dropoffHours: number): string {
  const from = a.startsAt ? longDt(a.startsAt) : '';
  switch (a.state) {
    case 'claim':
    case 'made':
      return 'vytváří se';
    case 'dormant':
      return 'začne platit odevzdáním klíče zákazníkem';
    case 'live':
      return a.kind === 'dropoff'
        ? `platí; zruší se ${dropoffHours} h po odevzdání`
        : a.kind === 'pickup'
          ? `platí od ${from}; zruší se uzavřením rezervace`
          : `${a.note || 'platí'}; zruší se uzavřením rezervace`;
    case 'ended':
      return `zrušen ${longDt(a.changedAt)}${a.note ? ` · ${a.note}` : ''}`;
  }
}

const STATE_LABEL: Record<string, string> = {
  unknown: '—',
  in_box: 'v boxu',
  with_driver: 'mimo box (řidič / kancelář)',
  collected: 'vyzvednut zákazníkem',
  taken: 'vyzvednut z boxu',
};

/**
 * The press reached the server and the key is being (or has been) made, but
 * the answer did not come back in time: the browser's retry met its own
 * operation id (`replayed_refresh`), or the connection broke (`uncertain`).
 * On 27 October 2026 booking 2284182 showed "akce už proběhla — obnovte
 * stránku" this way while the key appeared a minute later. The card now
 * says the key is being made and reloads itself instead (the owner's small
 * fix, handoff §4 item 3).
 */
const STILL_MAKING_CODES = new Set(['replayed_refresh', 'uncertain', 'op_unverifiable']);
const RELOAD_AFTER_MS = [1500, 5000, 15000, 40000];

export function KeycafeCard({ r }: { r: ReservationRow }) {
  const qc = useQueryClient();
  const { data, isPending, isError } = useQuery({
    queryKey: ['keycafe', r.id],
    queryFn: () => api.keycafe(r.id),
    retry: false,
  });
  const health = useQuery({ queryKey: ['health'], queryFn: () => api.health(), staleTime: 60_000 });
  const [done, setDone] = useState<string | null>(null);
  const [making, setMaking] = useState(false);
  const reload = () => Promise.all([qc.invalidateQueries({ queryKey: ['keycafe', r.id] }), qc.invalidateQueries({ queryKey: ['reservation', r.id] })]);
  const timers = useRef<number[]>([]);
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);
  const m = useMutation({
    mutationFn: () => api.createKeycafeKey(r.id),
    onSuccess: async (rc) => {
      setMaking(false);
      setDone(
        rc.codes
          ? `✔ Klíč vytvořen — odevzdání ${rc.codes.dropoff}, vyzvednutí ${rc.codes.pickup}, servis ${rc.codes.service}`
          : `✔ Klíč vytvořen, kód ${rc.code}`,
      );
      await qc.invalidateQueries();
    },
    onError: (e) => {
      if (!(e instanceof ApiError) || !STILL_MAKING_CODES.has(e.code)) return;
      // The key is being made without us: show it so, and read the card again a few times.
      setMaking(true);
      for (const ms of RELOAD_AFTER_MS) timers.current.push(window.setTimeout(() => void reload(), ms));
    },
  });
  const err = m.error;
  const stillMaking = making && err instanceof ApiError && STILL_MAKING_CODES.has(err.code);
  const message = stillMaking
    ? null
    : err instanceof ApiError
      ? err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message)
      : err instanceof Error
        ? err.message
        : null;

  const h = health.data;
  const keycafeOn =
    h?.keycafe === 'write' && h.keycafeKeys === true && (h.writeActions === 'all' || (Array.isArray(h.writeActions) && h.writeActions.includes('keycafe')));
  const live = ['new', 'pending', 'paid', 'verified'].includes(r.status);
  const code = data?.code || (r.keycafeDeparture ?? '').trim() || (r.keycafeArrival ?? '').trim() || '';
  // A key whose making stopped half-way (KeyCafe slow, a lost answer): the
  // same button finishes it — it never makes a second one.
  const unfinished = !!data?.key && !code;
  // The codes arrived while the card was saying "vytváří se": the message has done its job.
  if (stillMaking && code) setMaking(false);

  return (
    <section className="card detailcard">
      <h2>Keycafe</h2>
      {isPending ? (
        <div className="empty">Načítám…</div>
      ) : isError || !data ? (
        <div className="empty">Keycafe se nepodařilo načíst.</div>
      ) : (
        <>
          <table className="mini">
            <tbody>
              {data.accesses.length > 0 ? (
                data.accesses.map((a) => (
                  <tr key={a.kind}>
                    <td>{KIND_LABEL[a.kind]}</td>
                    <td>{a.code ? <b className={a.state === 'ended' ? 'kc-ended' : undefined}>{a.code}</b> : <span className="empty">—</span>}</td>
                    <td className="empty">{accessState(a, data.dropoffHours)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td>Kód</td>
                  <td>{code ? <b>{code}</b> : <span className="empty">bez kódu</span>}</td>
                  <td className="empty">
                    {data.key
                      ? data.key.keyId
                        ? `klíč ${data.key.keyId} vytvořen systémem ${longDt(data.key.createdAt)}`
                        : `vytváření klíče začalo ${longDt(data.key.createdAt)}, nedokončeno`
                      : code
                        ? 'klíč vytvořen ručně'
                        : ''}
                  </td>
                </tr>
              )}
              <tr>
                <td>Klíč</td>
                <td>{STATE_LABEL[data.keyState] ?? data.keyState}</td>
                <td className="empty">{data.bin ? `box ${data.bin}` : ''}</td>
              </tr>
            </tbody>
          </table>
          {data.events.length > 0 && (
            <table className="mini" style={{ marginTop: 8 }}>
              <tbody>
                {data.events.map((e: KeycafeEventRow) => (
                  <tr key={e.eventId}>
                    <td>{longDt(e.occurredAt)}</td>
                    <td>{e.type === 'DROPOFF' ? 'vhozen' : 'vyzvednut'}</td>
                    <td>{e.bin ? `box ${e.bin}` : ''}</td>
                    <td className="empty">{OUTCOME_LABEL[e.outcome] ?? e.outcome}{e.note ? ` · ${e.note}` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {!code && live && keycafeOn && !r.reslock && (
            <div style={{ marginTop: 10 }}>
              <button type="button" className="btn sm primary" disabled={m.isPending || stillMaking} onClick={() => { setDone(null); setMaking(false); m.mutate(); }}>
                {m.isPending ? 'Vytvářím…' : stillMaking ? 'Klíč se vytváří…' : unfinished ? 'Dokončit klíč v Keycafe' : 'Vytvořit klíč v Keycafe'}
              </button>
              {stillMaking && <span className="empty"> Klíč se vytváří v Keycafe, karta se za chvíli obnoví sama.</span>}
              {message && <span className="arrive-error"> {message}</span>}
            </div>
          )}
          {done && <div className="arrived-mark" style={{ marginTop: 8 }}>{done}</div>}
          <div className="empty" style={{ marginTop: 8 }}>Kód lze kdykoli opravit přes Opravit údaje (Keycafe příjezd / odjezd).</div>
        </>
      )}
    </section>
  );
}
